import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from 'components/Button/Button';
import PageWrapper from 'components/PageWrapper';
import CheckboxGroup from 'components/form/CheckboxGroup';
import RadioGroup from 'components/form/RadioGroup';
import Select from 'components/form/Select';
import { withAlert } from 'hooks/useAlert';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { getCustomColumns } from 'services/account.service';
import { getGroups } from 'services/groups.service';
import { TYPES, queueMsg } from 'services/rabbit.service';
import toast from 'utils/toast';

const ExportOptions = [
  {
    label: (
      <Box mt={2}>
        <div>Basic Fields</div>
        <Typography variant="caption" color="textSecondary">
          First Name, Last Name, Address Fields and Groups
        </Typography>
      </Box>
    ),
    value: 'basic',
  },
  {
    label: 'Select Fields',
    value: 'custom',
  },
];

const Fields = {
  contact_hash: 'Contact Hash',
  contact_first_name: 'First Name',
  contact_last_name: 'Last Name',
  contact_email: 'Email',
  primary_phone: 'Phone',
  primary_carrier: 'Carrier',
  secondary_phone: 'Alt Phone',
  secondary_carrier: 'Alt Carrier',
  contact_address1: 'Address1',
  contact_address2: 'Address2',
  contact_city: 'City',
  contact_state: 'State',
  contact_zip: 'Zip',
  contact_country: 'Country',
  contact_create_date: 'Date Added',
  contact_source: 'Source',
  contact_optout_email: 'Email Opt Out',
  opted_out_email_date: 'Email Opt Out Date',
  contact_optout_sms: 'SMS Opt Out',
  opted_out_sms_date: 'SMS Opt Out Date',
  contact_facebook_url: 'Facebook URL',
  contact_twitter_url: 'X URL',
  contact_instagram_url: 'Instagram URL',
  contact_linkedin_url: 'LinkedIn URL',
  groups: 'Groups',
};

class Export extends React.Component {
  state = {
    group: null,
    type: null,
    fields: [],
    columns: [],
  };

  componentDidMount() {
    getCustomColumns(this.props.account.hash).then((customColumns) => {
      const columns = Object.keys(Fields).map((key) => ({
        label: Fields[key],
        value: key,
      }));
      customColumns.forEach((c) => {
        columns.push({
          label: c.column_name,
          value: c.column_name,
        });
      });
      this.setState({
        columns,
      });
    });
  }

  onGroupSelect = (val) => {
    this.setState({ group: val });
  };

  onSearch = (value) => {
    return getGroups({ group_name: value }).then((resp) =>
      resp.map((g) => ({ label: g.group_name, value: g.group_id })),
    );
  };

  onTypeChange = (e, val) => {
    this.setState({
      type: val,
    });
  };

  onFieldsChange = (e) => {
    const { value } = e.target;
    this.setState({
      fields: value,
    });
  };

  onExport = (type) => async () => {
    const { alert, t } = this.props;
    const { group, fields, type: exportType } = this.state;
    const isContacts = type === 'contacts';

    try {
      const params = {
        report: type,
      };
      if (isContacts) {
        params.group_id = group?.value;
        if (exportType === 'custom') {
          params.fields = fields;
        } else {
          params.fields = [
            'contact_first_name',
            'contact_last_name',
            'contact_address1',
            'contact_address2',
            'contact_city',
            'contact_state',
            'contact_zip',
            'groups',
          ];
        }
      }
      await queueMsg(TYPES.EXPORT_REPORT, params);
      alert.confirm({
        severity: 'success',
        showCancelButton: false,
        confirmationText: 'Close',
        title: t('export-success-title', 'Your export is queued'),
        description: t(
          'export-success-description',
          'We will send you an email shortly with your data as a .csv file.',
        ),
      });
      this.setState({
        fields: [],
        type: null,
      });
    } catch (e) {
      toast.error(e.message);
    }
  };

  render() {
    const { t } = this.props;
    const { group, type, fields, columns } = this.state;
    return (
      <PageWrapper
        breadcrumbs={[{ title: 'Export Data' }]}
        title={t('export-data', 'Export Data')}
      >
        <Helmet title={t('export-page-title', 'Settings > Export')} />
        <Card variant="outlinedVariant" sx={{ mb: 3 }}>
          <CardContent>
            <Typography gutterBottom variant="h4" fontWeight={600}>
              {t(
                'export-all-members',
                'Export the data for all of your members.',
              )}
            </Typography>
            <Typography>
              {t(
                'export-members-hint',
                'Select a group to optionally export only the members for that group.',
              )}
            </Typography>
            <Select
              isClearable
              fullWidth
              label={t('export-select-group-label', 'Select Group to Export')}
              value={group}
              onChange={this.onGroupSelect}
              loadOptions={this.onSearch}
              defaultOptions
              placeholder={t(
                'export-select-group-placeholder',
                'Click Here to Select a Group',
              )}
            />
            <Box mb={3}>
              <RadioGroup
                margin="none"
                label="Select Content to Export"
                options={ExportOptions}
                value={type}
                onChange={this.onTypeChange}
              />
              <Box
                sx={{
                  '& .MuiFormGroup-root': {
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                  },
                }}
              >
                {type === 'custom' && (
                  <CheckboxGroup
                    margin="none"
                    size="small"
                    options={columns}
                    value={fields}
                    onChange={this.onFieldsChange}
                  />
                )}
              </Box>
            </Box>
            <Button disabled={!type} onClick={this.onExport('contacts')}>
              {t('common:export', 'Export')}
            </Button>
          </CardContent>
        </Card>
        <Card variant="outlinedVariant">
          <CardContent>
            <Typography gutterBottom variant="h4" fontWeight={600}>
              {t('export-text-messages', 'Export Text Messages.')}
            </Typography>
            <Typography paragraph>
              {t(
                'export-text-messages-hint',
                'Export most recent 1500 incoming text messages.',
              )}
            </Typography>
            <Button onClick={this.onExport('incoming_sms')}>
              {t('common:export', 'Export')}
            </Button>
          </CardContent>
        </Card>
      </PageWrapper>
    );
  }
}

export default connect((state) => state.settings)(
  withTranslation('settings')(withAlert(Export)),
);
